import type { PostingCategoryKind, PostingNode } from '@revelio/data-access';

import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type ListFilter,
  type StaticListFilter,
} from '../../../filters/types/filters';
import { isListFilter, isStaticListFilter } from '../filter-type-guards';
import { idsToNode, wrapExprNode } from './node-builders';
import { PostingDiscoveryTags } from './tags';

const extractNumericSelections = (
  filter: ListFilter | StaticListFilter | undefined
): number[] => {
  if (!filter) return [];

  const selections = filter.selections;
  if (!Array.isArray(selections)) return [];

  return selections
    .map((s) => {
      const id = (s as { id?: string | number })?.id;
      const n = typeof id === 'string' ? Number(id) : id;
      return typeof n === 'number' && Number.isFinite(n) ? n : null;
    })
    .filter((n): n is number => n !== null);
};

const buildListFilter = (
  filter: ListFilter | StaticListFilter,
  kind: PostingCategoryKind
): PostingNode | null => {
  const ids = extractNumericSelections(filter);
  const exprNode = idsToNode(ids, kind);
  if (!exprNode) return null;

  const isNot = 'isNot' in filter ? filter.isNot : false;
  return wrapExprNode(exprNode, isNot);
};

const findAndBuildListFilter = (
  filters: Filter[],
  filterId: FilterId,
  kind: PostingCategoryKind
): PostingNode | null => {
  const filter = findFilterById(filters, filterId);
  if (!isListFilter(filter) && !isStaticListFilter(filter)) {
    return null;
  }
  return buildListFilter(filter, kind);
};

/** Build the seniority filter node from filter state. */
export const buildSeniorityFilter = (filters: Filter[]): PostingNode | null =>
  findAndBuildListFilter(
    filters,
    FilterId.SENIORITY,
    PostingDiscoveryTags.Seniority
  );

export const buildMultiSeniorityFilter = (
  filters: Filter[]
): PostingNode | PostingNode[] | null => {
  const multiSeniority = findFilterById(filters, FilterId.MULTI_SENIORITY);
  if (!multiSeniority || !isMultiFilter(multiSeniority)) {
    return null;
  }
  if (multiSeniority.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiSeniority.instances
    .filter((inst): inst is ListFilter => inst?.type === FilterType.LIST)
    .map((instance) =>
      buildListFilter(instance, PostingDiscoveryTags.Seniority)
    )
    .filter((node): node is PostingNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }
  return instanceNodes;
};

export const buildRemoteTypeFilter = (filters: Filter[]): PostingNode | null =>
  findAndBuildListFilter(
    filters,
    FilterId.REMOTE_TYPE,
    PostingDiscoveryTags.RemoteType
  );

export const buildMultiRemoteTypeFilter = (
  filters: Filter[]
): PostingNode | PostingNode[] | null => {
  const multiRemoteType = findFilterById(filters, FilterId.MULTI_REMOTE_TYPE);
  if (!multiRemoteType || !isMultiFilter(multiRemoteType)) {
    return null;
  }
  if (multiRemoteType.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiRemoteType.instances
    .filter((inst): inst is ListFilter => inst?.type === FilterType.LIST)
    .map((instance) =>
      buildListFilter(instance, PostingDiscoveryTags.RemoteType)
    )
    .filter((node): node is PostingNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }
  return instanceNodes;
};
